import { OrgPricingResponse } from '@goldcast/api/backend';

const getters = {
  getPricingConfig(state): OrgPricingResponse {
    return state?.orgPricingConfig;
  },
  getIsDriftEnabled(state): boolean {
    if (state?.orgPricingConfig?.tier === 'NO_TIER') {
      return true;
    }

    return state?.orgPricingConfig?.pricing_config?.is_drift_enabled;
  },
  getIsSlackSalesforceEnabled(state): boolean {
    if (state?.orgPricingConfig?.tier === 'NO_TIER') {
      return true;
    }

    return state?.orgPricingConfig?.pricing_config?.is_slack_salesforce_enabled;
  },
  getIsWistiaEnabled(state): boolean {
    if (state?.orgPricingConfig?.tier === 'NO_TIER') {
      return true;
    }

    return state?.orgPricingConfig?.pricing_config?.is_wistia_enabled;
  },
  getIsSlackEnabled(state): boolean {
    if (state?.orgPricingConfig?.tier === 'NO_TIER') {
      return true;
    }

    return state?.orgPricingConfig?.pricing_config?.is_slack_enabled;
  },
  getIsPardotEnabled(state): boolean {
    if (state?.orgPricingConfig?.tier === 'NO_TIER') {
      return true;
    }

    return state?.orgPricingConfig?.pricing_config?.is_pardot_enabled;
  },
  getIsEloquaEnabled(state): boolean {
    if (state?.orgPricingConfig?.tier === 'NO_TIER') {
      return true;
    }

    return state?.orgPricingConfig?.pricing_config?.is_eloqua_enabled;
  },
  getRegCapEnabled(state, getters, rootState, rootGetters): boolean {
    const currentOrg = rootGetters['org/getCurrentOrg'];

    if (state?.orgPricingConfig?.tier === 'NO_TIER') {
      return currentOrg?.enable_registrant_cap;
    }

    return state?.orgPricingConfig?.pricing_config?.enable_registrant_cap;
  },
  getIsSalesforceEnabled(state): boolean {
    if (state?.orgPricingConfig?.tier === 'NO_TIER') {
      return true;
    }

    return state?.orgPricingConfig?.pricing_config?.is_salesforce_enabled;
  },
  getIsMarketoEnabled(state): boolean {
    if (state?.orgPricingConfig?.tier === 'NO_TIER') {
      return true;
    }

    return state?.orgPricingConfig?.pricing_config?.is_marketo_enabled;
  },
  getMarketoHubspotRegistrationFormEnabled(state): boolean {
    if (state?.orgPricingConfig?.tier === 'NO_TIER') {
      return true;
    }

    return state?.orgPricingConfig?.pricing_config?.marketo_hubspot_registration_form_enabled;
  },
  getIsMarketingTagsEnabled(state): boolean {
    if (state?.orgPricingConfig?.tier === 'NO_TIER') {
      return true;
    }

    return state?.orgPricingConfig?.pricing_config?.is_marketing_tags_enabled;
  },
  getIsWaitlistEnabled(state): boolean {
    if (state?.orgPricingConfig?.tier === 'NO_TIER') {
      return true;
    }

    return state?.orgPricingConfig?.pricing_config?.is_waitlist_enabled;
  },
  getIsMultiDayEnabled(state): boolean {
    if (state?.orgPricingConfig?.tier === 'NO_TIER') {
      return true;
    }

    return state?.orgPricingConfig?.pricing_config?.is_multi_day_enabled;
  },
  getIsMultiSessionEnabled(state): boolean {
    if (state?.orgPricingConfig?.tier === 'NO_TIER') {
      return true;
    }

    return state?.orgPricingConfig?.pricing_config?.is_multi_session_enabled;
  },
  getIsMultiTrackEnabled(state): boolean {
    if (state?.orgPricingConfig?.tier === 'NO_TIER') {
      return true;
    }

    return state?.orgPricingConfig?.pricing_config?.is_multi_track_enabled;
  },
  getIsPrerecordedSessionEnabled(state): boolean {
    if (state?.orgPricingConfig?.tier === 'NO_TIER') {
      return true;
    }

    return state?.orgPricingConfig?.pricing_config?.is_prerecorded_session_enabled;
  },
  getCanCreateRtmpSession(state): boolean {
    if (state?.orgPricingConfig?.tier === 'NO_TIER') {
      return true;
    }

    return state?.orgPricingConfig?.pricing_config?.can_create_rtmp_broadcast;
  },
  getIsEnabledRtmpOut(state): boolean {
    if (state?.orgPricingConfig?.tier === 'NO_TIER') {
      return true;
    }

    return state?.orgPricingConfig?.pricing_config?.enabled_rtmp_out;
  },
  getIsEmbeddedEventEnabled(state): boolean {
    return state?.orgPricingConfig?.pricing_config?.embedded_events_enabled ?? false;
  },
  getIsEnableLiveCaptionsTranslations(state): boolean {
    if (state?.orgPricingConfig?.tier === 'NO_TIER') {
      return true;
    }

    return state?.orgPricingConfig?.pricing_config?.enable_live_captions_translations;
  },
  getIsTranslationsEnabled(state): boolean {
    if (state?.orgPricingConfig?.tier === 'NO_TIER') {
      return true;
    }

    return state?.orgPricingConfig?.pricing_config?.is_translations_enabled;
  },
  getIsCrossEventReportEnabled(state): boolean {
    if (state?.orgPricingConfig?.tier === 'NO_TIER') {
      return true;
    }

    return state?.orgPricingConfig?.pricing_config?.cross_event_report_available;
  },
  getIsPageDomainEnabled(state, getters, rootState, rootGetters): boolean {
    const currentOrg = rootGetters['org/getCurrentOrg'];
    if (state?.orgPricingConfig?.tier === 'NO_TIER') {
      return currentOrg?.allow_whitelabel;
    }

    return state?.orgPricingConfig?.pricing_config?.allow_whitelabel;
  },
  getIsRoiDashboardEnabled(state): boolean {
    if (state?.orgPricingConfig?.tier === 'NO_TIER') {
      return true;
    }

    return state?.orgPricingConfig?.pricing_config?.is_roi_dashboard_enabled;
  },
  getAllowHybridEvents(state): boolean {
    if (state?.orgPricingConfig?.tier === 'NO_TIER') {
      return true;
    }

    return state?.orgPricingConfig?.pricing_config?.allow_hybrid_events;
  },
  getIsBoothsTabEnabled(state, getters, rootState, rootGetters): boolean {
    const currentOrg = rootGetters['org/getCurrentOrg'];
    if (state?.orgPricingConfig?.tier === 'NO_TIER') {
      return currentOrg?.is_booths_tab_enabled;
    }

    return state?.orgPricingConfig?.pricing_config?.is_booths_tab_enabled;
  },
  getIsSeriesEnabled(state): boolean {
    if (state?.orgPricingConfig?.tier === 'NO_TIER') {
      return true;
    }

    return state?.orgPricingConfig?.pricing_config?.is_series_enabled;
  },
  getIsHubPageEnabled(state): boolean {
    if (state?.orgPricingConfig?.tier === 'NO_TIER') {
      return true;
    }

    return state?.orgPricingConfig?.pricing_config?.can_create_hub_pages;
  },
  getIsTemplateEnabled(state): boolean {
    if (state?.orgPricingConfig?.tier === 'NO_TIER') {
      return true;
    }

    return state?.orgPricingConfig?.pricing_config?.show_templates;
  },
  getIsMagicLinkSyncEnabled(state): boolean {
    if (state?.orgPricingConfig?.tier === 'NO_TIER') {
      return true;
    }

    return state?.orgPricingConfig?.pricing_config?.is_magiclink_sync_to_map_enabled;
  },
  getEventLengthInMinutes(state): boolean {
    return state?.orgPricingConfig?.pricing_config?.event_length_in_minutes;
  },
  getInPersonEventsEnabled(state, getters, rootState, rootGetters): boolean {
    const currentOrg = rootGetters['org/getCurrentOrg'];
    if (state?.orgPricingConfig?.tier === 'NO_TIER') {
      return currentOrg?.in_person_events_enabled;
    }

    return state?.orgPricingConfig?.pricing_config?.in_person_events_enabled;
  },
  getAllowTokenCreation(state, getters, rootState, rootGetters): boolean {
    const currentOrg = rootGetters['org/getCurrentOrg'];
    if (state?.orgPricingConfig?.tier === 'NO_TIER') {
      return currentOrg?.allow_token_creation;
    }
    return state.orgPricingConfig?.pricing_config?.allow_token_creation;
  },
  getIsWebhookEnabled(state, getters, rootState, rootGetters): boolean {
    const currentOrg = rootGetters['org/getCurrentOrg'];
    if (state?.orgPricingConfig?.tier === 'NO_TIER') {
      return currentOrg?.is_webhook_enabled;
    }
    return state.orgPricingConfig?.pricing_config?.is_webhook_enabled;
  },
  getIsRepeatingEventsEnabled(state): boolean {
    if (state?.orgPricingConfig?.tier === 'NO_TIER') {
      return true;
    }
    return state.orgPricingConfig?.pricing_config?.is_repeating_events_enabled;
  },
  getIsPermissionsEnabled(state, getters, rootState, rootGetters): boolean {
    const currentOrg = rootGetters['org/getCurrentOrg'];

    if (state?.orgPricingConfig?.tier === 'NO_TIER') {
      return currentOrg?.permissions_enabled;
    }

    return state.orgPricingConfig?.pricing_config?.permissions_enabled;
  },
  getIsSelfServeReportingEnabled(state, getters, rootState, rootGetters): boolean {
    const currentOrg = rootGetters['org/getCurrentOrg'];
    if (state?.orgPricingConfig?.tier === 'NO_TIER') {
      return currentOrg?.noTierCustomReporting;
    }

    return state?.orgPricingConfig?.pricing_config?.is_self_serve_reporting_enabled;
  },
  getIsOneClickEnabled(state): boolean {
    if (state?.orgPricingConfig?.tier === 'NO_TIER') {
      return true;
    }

    return state?.orgPricingConfig?.pricing_config?.is_one_click_enabled || false;
  },
  getVoiceProfileCount(state): number {
    return state.orgPricingConfig?.pricing_config?.voice_profile_count ?? -1;
  },
  getAllowCaptionTranslation(state): boolean {
    if (state?.orgPricingConfig?.tier === 'NO_TIER') {
      return false;
    }

    return state?.orgPricingConfig?.pricing_config?.allow_caption_translation ?? false;
  },
  getIsAiFeaturesEnabled(state): boolean {
    if (state?.orgPricingConfig?.tier === 'NO_TIER') {
      return true;
    }

    return state?.orgPricingConfig?.pricing_config?.ai_features_enabled ?? false;
  }
};

export default getters;
